// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  engineApiV2,
  EngineTagTypes,
} from '@shared/data-access/api-base/citec-engine-api';
import { rawApi } from '@shared/data-access/api/engine/raw-api';
import type {
  ListTagsParams,
  ListTags,
  CreateTag,
  Tag,
  DeleteTagParams,
  SyncTagAssignments,
  ListTagAssignments,
  SearchTagAssignments,
  ListSearchTags,
} from '@shared/data-access/types/engine-types';

export const tagsApi = engineApiV2.injectEndpoints({
  endpoints: build => ({
    getTags: build.query<ListTags, ListTagsParams>({
      queryFn: async (params, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.getTagsRaw.initiate(params, {
            forceRefetch: api.forced,
          }),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const list = result.data ?? { data: [], count: 0 };
        return { data: list };
      },
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({ tag_id }) => ({
                type: EngineTagTypes.Tag,
                id: tag_id,
              })),
              { type: EngineTagTypes.Tag, id: 'LIST' },
            ]
          : [{ type: EngineTagTypes.Tag, id: 'LIST' }],
    }),

    createTag: build.mutation<Tag, CreateTag>({
      queryFn: async (body, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.createTagRaw.initiate(body),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: result.data as Tag };
      },
      invalidatesTags: [{ type: EngineTagTypes.Tag, id: 'LIST' }],
    }),

    deleteTag: build.mutation<void, DeleteTagParams>({
      queryFn: async (arg, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.deleteTagRaw.initiate(arg),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: undefined };
      },
      invalidatesTags: (result, error, arg) => [
        { type: EngineTagTypes.Tag, id: arg.tag_id },
        { type: EngineTagTypes.Tag, id: 'LIST' },
      ],
    }),

    syncTags: build.mutation<ListTagAssignments, SyncTagAssignments>({
      queryFn: async (body, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.syncTagsRaw.initiate(body),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return {
          data: result.data as ListTagAssignments,
        };
      },
      invalidatesTags: [{ type: EngineTagTypes.TagAssignment, id: 'LIST' }],
    }),

    searchTagAssignments: build.query<ListSearchTags, SearchTagAssignments>({
      queryFn: async (body, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.searchTagAssignmentsRaw.initiate(body),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return {
          data: result.data as ListSearchTags,
        };
      },
      providesTags: [{ type: EngineTagTypes.TagAssignment, id: 'LIST' }],
    }),
  }),
});
